import React from 'react'
import { Modal, Descriptions, Tag, Typography, Divider, Table, Badge } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import moment from 'moment'

const { Title, Text } = Typography

const StyledModal = styled(Modal)`
	.ant-modal-body {
		max-height: 70vh;
		overflow-y: auto;
	}
`

const SectionTitle = styled(Title)`
	margin-top: 16px !important;
	margin-bottom: 12px !important;
`

interface PCInfoModalProps {
	visible: boolean
	onClose: () => void
	data: string | null
}

interface SystemInfo {
	device?: string
	system?: {
		isVirtual?: boolean
		model?: string
		manufacturer?: string
		os?: {
			type?: string
			platform?: string
			release?: string
			arch?: string
			caption?: string
			hostname?: string
		}
		cpu?: {
			model?: string
			cores?: number
			speed?: number
		}
		memory?: {
			totalGB?: number
			freeGB?: number
			usedGB?: number
			usagePercent?: string
		}
		network?: {
			interfaces?: Array<{
				name?: string
				ip?: string
				mac?: string
			}>
			proxy?: {
				http?: string | null
				https?: string | null
			}
		}
		isServerPC?: boolean
		remoteTools?: {
			anydesk?: boolean
			teamviewer?: boolean
			chromeRemote?: boolean
		}
	}
	version?: string
	time?: number
	f?: number
}

const PCInfoModal: React.FC<PCInfoModalProps> = ({ visible, onClose, data }) => {
	const [systemInfo, setSystemInfo] = React.useState<SystemInfo | null>(null)
	const [parseError, setParseError] = React.useState(false)

	React.useEffect(() => {
		if (data) {
			try {
				const parsed = JSON.parse(data)
				setSystemInfo(parsed)
				setParseError(false)
			} catch (e) {
				setParseError(true)
				setSystemInfo(null)
			}
		}
	}, [data])

	if (!systemInfo || parseError) {
		return (
			<Modal
				title="PC 시스템 정보"
				open={visible}
				onCancel={onClose}
				footer={null}
				width={800}>
				<Text type="danger">시스템 정보를 파싱할 수 없습니다.</Text>
			</Modal>
		)
	}

	const renderBooleanIcon = (value?: boolean) => {
		if (value === undefined || value === null) return <Text type="secondary">정보 없음</Text>
		return value ? (
			<Tag color="success" icon={<CheckCircleOutlined />}>
				예
			</Tag>
		) : (
			<Tag color="default" icon={<CloseCircleOutlined />}>
				아니오
			</Tag>
		)
	}

	const networkColumns = [
		{
			title: '인터페이스명',
			dataIndex: 'name',
			key: 'name',
			render: (text: string) => text || <Text type="secondary">N/A</Text>,
		},
		{
			title: 'IP 주소',
			dataIndex: 'ip',
			key: 'ip',
			render: (text: string) => {
				if (!text || text === 'N/A') return <Text type="secondary">N/A</Text>
				return <Tag color="blue">{text}</Tag>
			},
		},
		{
			title: 'MAC 주소',
			dataIndex: 'mac',
			key: 'mac',
			render: (text: string) => {
				if (!text || text === 'N/A') return <Text type="secondary">N/A</Text>
				return <Text code>{text}</Text>
			},
		},
	]

	const remoteToolsData = systemInfo?.system?.remoteTools
		? [
				{ tool: 'AnyDesk', installed: systemInfo.system.remoteTools.anydesk },
				{ tool: 'TeamViewer', installed: systemInfo.system.remoteTools.teamviewer },
				{ tool: 'Chrome Remote', installed: systemInfo.system.remoteTools.chromeRemote },
		  ]
		: []

	const remoteToolsColumns = [
		{
			title: '원격 도구',
			dataIndex: 'tool',
			key: 'tool',
		},
		{
			title: '설치 여부',
			dataIndex: 'installed',
			key: 'installed',
			render: (installed: boolean) => renderBooleanIcon(installed),
		},
	]

	return (
		<StyledModal
			title={
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<span>PC 시스템 정보</span>
					{systemInfo?.device && (
						<Tag color={systemInfo.device === 'HTS' ? 'blue' : 'purple'}>
							{systemInfo.device}
						</Tag>
					)}
					{systemInfo?.version && <Tag color="green">v{systemInfo.version}</Tag>}
				</div>
			}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={900}>
			{/* 기본 정보 */}
			<Descriptions bordered size="small" column={2}>
				{systemInfo?.time && (
					<Descriptions.Item label="접속 시간" span={2}>
						{moment(systemInfo.time).format('YYYY-MM-DD HH:mm:ss')}
					</Descriptions.Item>
				)}
				{systemInfo?.f && (
					<Descriptions.Item label="창 번호" span={2}>
						<Tag color="orange">#{systemInfo.f}번창</Tag>
					</Descriptions.Item>
				)}
			</Descriptions>

			{/* 하드웨어 정보 */}
			{systemInfo?.system && (
				<>
					<Divider />
					<SectionTitle level={5}>하드웨어 정보</SectionTitle>
					<Descriptions bordered size="small" column={2}>
						{systemInfo.system.manufacturer && (
							<Descriptions.Item label="제조사">
								{systemInfo.system.manufacturer}
							</Descriptions.Item>
						)}
						{systemInfo.system.model && (
							<Descriptions.Item label="모델">
								{systemInfo.system.model}
							</Descriptions.Item>
						)}
						{systemInfo.system.isVirtual !== undefined && (
							<Descriptions.Item label="가상 머신">
								{renderBooleanIcon(systemInfo.system.isVirtual)}
							</Descriptions.Item>
						)}
						{systemInfo.system.isServerPC !== undefined && (
							<Descriptions.Item label="서버 PC">
								{renderBooleanIcon(systemInfo.system.isServerPC)}
							</Descriptions.Item>
						)}
					</Descriptions>
				</>
			)}

			{/* 운영체제 정보 */}
			{systemInfo?.system?.os && (
				<>
					<Divider />
					<SectionTitle level={5}>운영체제 정보</SectionTitle>
					<Descriptions bordered size="small" column={2}>
						{systemInfo.system.os.hostname && (
							<Descriptions.Item label="호스트명" span={2}>
								<Tag color="cyan">{systemInfo.system.os.hostname}</Tag>
							</Descriptions.Item>
						)}
						{systemInfo.system.os.caption && (
							<Descriptions.Item label="OS" span={2}>
								{systemInfo.system.os.caption}
							</Descriptions.Item>
						)}
						{systemInfo.system.os.type && (
							<Descriptions.Item label="타입">
								{systemInfo.system.os.type}
							</Descriptions.Item>
						)}
						{systemInfo.system.os.platform && (
							<Descriptions.Item label="플랫폼">
								{systemInfo.system.os.platform}
							</Descriptions.Item>
						)}
						{systemInfo.system.os.arch && (
							<Descriptions.Item label="아키텍처">
								{systemInfo.system.os.arch}
							</Descriptions.Item>
						)}
						{systemInfo.system.os.release && (
							<Descriptions.Item label="빌드 번호">
								{systemInfo.system.os.release}
							</Descriptions.Item>
						)}
					</Descriptions>
				</>
			)}

			{/* CPU 정보 */}
			{systemInfo?.system?.cpu && (
				<>
					<Divider />
					<SectionTitle level={5}>CPU 정보</SectionTitle>
					<Descriptions bordered size="small" column={2}>
						{systemInfo.system.cpu.model && (
							<Descriptions.Item label="모델" span={2}>
								{systemInfo.system.cpu.model}
							</Descriptions.Item>
						)}
						{systemInfo.system.cpu.cores && (
							<Descriptions.Item label="코어 수">
								<Badge count={systemInfo.system.cpu.cores} showZero color="blue" />
							</Descriptions.Item>
						)}
						{systemInfo.system.cpu.speed && (
							<Descriptions.Item label="클럭 속도">
								{systemInfo.system.cpu.speed} MHz
							</Descriptions.Item>
						)}
					</Descriptions>
				</>
			)}

			{/* 메모리 정보 */}
			{systemInfo?.system?.memory && (
				<>
					<Divider />
					<SectionTitle level={5}>메모리 정보</SectionTitle>
					<Descriptions bordered size="small" column={2}>
						{systemInfo.system.memory.totalGB && (
							<Descriptions.Item label="총 메모리">
								{systemInfo.system.memory.totalGB} GB
							</Descriptions.Item>
						)}
						{systemInfo.system.memory.usedGB && (
							<Descriptions.Item label="사용 중">
								{systemInfo.system.memory.usedGB} GB
							</Descriptions.Item>
						)}
						{systemInfo.system.memory.freeGB && (
							<Descriptions.Item label="사용 가능">
								{systemInfo.system.memory.freeGB} GB
							</Descriptions.Item>
						)}
						{systemInfo.system.memory.usagePercent && (
							<Descriptions.Item label="사용률">
								<Tag color={parseFloat(systemInfo.system.memory.usagePercent) > 80 ? 'red' : 'green'}>
									{systemInfo.system.memory.usagePercent}%
								</Tag>
							</Descriptions.Item>
						)}
					</Descriptions>
				</>
			)}

			{/* 네트워크 정보 */}
			{systemInfo?.system?.network?.interfaces && systemInfo.system.network.interfaces.length > 0 && (
				<>
					<Divider />
					<SectionTitle level={5}>네트워크 인터페이스</SectionTitle>
					<Table
						columns={networkColumns}
						dataSource={systemInfo.system.network.interfaces}
						pagination={false}
						size="small"
						rowKey={(record, index) => `network-${index}`}
					/>
				</>
			)}

			{/* 프록시 정보 */}
			{systemInfo?.system?.network?.proxy && (
				<>
					<Divider />
					<SectionTitle level={5}>프록시 설정</SectionTitle>
					<Descriptions bordered size="small" column={2}>
						<Descriptions.Item label="HTTP 프록시">
							{systemInfo.system.network.proxy.http || (
								<Text type="secondary">설정 안됨</Text>
							)}
						</Descriptions.Item>
						<Descriptions.Item label="HTTPS 프록시">
							{systemInfo.system.network.proxy.https || (
								<Text type="secondary">설정 안됨</Text>
							)}
						</Descriptions.Item>
					</Descriptions>
				</>
			)}

			{/* 원격 도구 정보 */}
			{remoteToolsData.length > 0 && (
				<>
					<Divider />
					<SectionTitle level={5}>원격 접속 도구</SectionTitle>
					<Table
						columns={remoteToolsColumns}
						dataSource={remoteToolsData}
						pagination={false}
						size="small"
						rowKey="tool"
					/>
				</>
			)}
		</StyledModal>
	)
}

export default PCInfoModal
